import React from 'react';
import { 
  FaLightbulb, 
  FaHeart, 
  FaGlobe, 
  FaChartBar,
  FaUsers,
  FaShieldAlt
} from 'react-icons/fa';

const OurMission = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Mission</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Creating a sustainable future through innovative waste management solutions
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Mission Statement */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission Statement</h2>
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              "To revolutionize waste management by creating a transparent, efficient, and sustainable 
              platform that connects waste producers with material validators, transforming waste into 
              valuable resources while promoting environmental responsibility and economic opportunity."
            </p>
            <div className="border-l-4 border-blue-600 pl-6">
              <p className="text-lg text-gray-600 italic">
                "Every piece of waste has value. Our mission is to unlock that value for the benefit 
                of our planet and our communities."
              </p>
            </div>
          </div>
        </div>

        {/* Vision */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg text-white p-8 mb-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Our Vision</h2>
            <p className="text-xl text-green-100 max-w-4xl mx-auto leading-relaxed">
              To become the leading platform for sustainable waste management, creating a world where 
              waste is seen as a resource, not a problem. We envision a future where every community 
              has access to efficient waste management solutions that benefit both the environment 
              and the economy.
            </p>
          </div>
        </div>

        {/* Core Objectives */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Core Objectives</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaLightbulb className="h-12 w-12 text-yellow-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">
                Continuously develop and implement cutting-edge technology to improve waste 
                management processes and user experience.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaHeart className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Environmental Care</h3>
              <p className="text-gray-600">
                Prioritize environmental sustainability in all our operations, reducing waste 
                in landfills and promoting circular economy principles.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaGlobe className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Global Impact</h3>
              <p className="text-gray-600">
                Expand our platform globally to create a worldwide network of sustainable 
                waste management solutions.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaChartBar className="h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Economic Growth</h3>
              <p className="text-gray-600">
                Create economic opportunities for individuals and communities through 
                sustainable waste management practices.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaUsers className="h-12 w-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Building</h3>
              <p className="text-gray-600">
                Foster strong communities around sustainable practices, connecting people 
                who share our environmental values.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
                <FaShieldAlt className="h-12 w-12 text-indigo-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Trust & Transparency</h3>
              <p className="text-gray-600">
                Maintain the highest standards of trust and transparency in all our 
                operations and user interactions.
              </p>
            </div>
          </div>
        </div>

        {/* Impact Goals */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Impact Goals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Environmental Goals</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Reduce landfill waste by 70% in our operating regions</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Increase recycling rates by 50% through our platform</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Promote circular economy practices across all sectors</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Achieve carbon neutrality in our operations by 2025</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Social Goals</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Create 10,000+ jobs in the waste management sector</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Train 5,000+ certified material validators</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Support 1,000+ small businesses through our platform</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-600">Educate 100,000+ people about sustainable waste management</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Join Our Mission</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Be part of the solution. Whether you're a seller, agent, or simply someone who cares 
            about the environment, you can contribute to our mission of creating a more sustainable future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors">
              Become a Seller
            </button>
            <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
              Join as Agent
            </button>
            <button className="bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors">
              Learn More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurMission;
